import React, { useState, useEffect } from "react";
import {
  Modal,
  Select,
  List,
  notification,
  Image,
  Row,
  Col,
  Spin,
  Checkbox,
} from "antd";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import http from "../../../../helpers/http";
import { DragHandle } from "../../../../components/DragHandle";

const { Option } = Select;

const Ordering = ({
  isOpen,
  onClose,
  brands,
  allCategory,
  moduleName,
  image_url,
  base_url,
}) => {
  const [selectedBrand, setSelectedBrand] = useState();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState();
  const [datalist, setDatalist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [showFeatured, setShowFeatured] = useState(false);

  // Preselect first brand and its first category when modal opens
  useEffect(() => {
    if (isOpen && brands && brands.length > 0) {
      setSelectedBrand(brands[0]._id);
      const filtered = allCategory.filter(
        (cat) => cat.brand?._id === brands[0]._id
      );
      setCategories(filtered);
      if (filtered.length > 0) {
        setSelectedCategory(filtered[0]._id);
      } else {
        setSelectedCategory(undefined);
      }
      setShowFeatured(false);
      setDatalist([]);
    }
  }, [isOpen, brands, allCategory]);

  // Filter categories when brand changes
  useEffect(() => {
    if (!showFeatured && selectedBrand && allCategory) {
      const filtered = allCategory.filter(
        (cat) => cat.brand?._id === selectedBrand
      );
      setCategories(filtered);
      if (filtered.length > 0) {
        setSelectedCategory(filtered[0]._id);
      } else {
        setSelectedCategory(undefined);
      }
      setDatalist([]);
    }
  }, [selectedBrand, allCategory, showFeatured]);

  // Fetch products when brand/category changes or showFeatured toggles
  useEffect(() => {
    const fetchProducts = async () => {
      setFetching(true);
      try {
        if (showFeatured) {
          const { data } = await http.get(`${base_url}/featured`);
          setDatalist(data || []);
        } else if (selectedBrand && selectedCategory) {
          const { data } = await http.get(
            `${base_url}/byCategory/${selectedCategory}`
          );
          setDatalist(data || []);
        } else {
          setDatalist([]);
        }
      } catch (e) {
        setDatalist([]);
      }
      setFetching(false);
    };
    if (isOpen) fetchProducts();
  }, [selectedBrand, selectedCategory, base_url, showFeatured, isOpen]);

  const handleOrder = async (updatedData) => {
    setLoading(true);
    try {
      let url = showFeatured ? `${base_url}/featured-sort` : `${base_url}/sort`;
      let payload;
      if (showFeatured) {
        payload = {
          products: updatedData.map((item, idx) => ({
            _id: item._id,
            featured_sort_order: idx,
          })),
        };
      } else {
        payload = {
          products: updatedData.map((item, idx) => ({
            _id: item._id,
            sort_order: idx,
          })),
        };
      }
      const { data } = await http.put(url, payload);
      if (data) {
        notification["success"]({
          message: `${moduleName} Sorting Updated Successfully`,
        });
        onClose();
      }
    } catch (e) {
      notification["error"]({ message: "Failed to update sorting" });
    }
    setLoading(false);
  };

  return (
    <Modal
      title={`${moduleName}s Ordering`}
      visible={isOpen}
      onOk={() => handleOrder(datalist)}
      okText="Update"
      confirmLoading={loading}
      onCancel={onClose}
      width={600}
    >
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <>
            <p>Brands</p>
            <Select
              placeholder="Select Brand"
              value={selectedBrand}
              onChange={setSelectedBrand}
              style={{ width: "100%" }}
              // allowClear
              disabled={showFeatured}
            >
              {brands &&
                brands.map((brand) => (
                  <Option key={brand._id} value={brand._id}>
                    {brand.name}
                  </Option>
                ))}
            </Select>
          </>
        </Col>
        <Col span={12}>
          <>
            <p>Categories</p>
            <Select
              placeholder="Select Category"
              value={selectedCategory}
              onChange={setSelectedCategory}
              style={{ width: "100%" }}
              disabled={showFeatured || !categories.length}
              // allowClear
            >
              {categories &&
                categories.map((cat) => (
                  <Option key={cat._id} value={cat._id}>
                    {cat.name}
                  </Option>
                ))}
            </Select>
          </>
        </Col>
      </Row>
      <Checkbox
        checked={showFeatured}
        onChange={(e) => setShowFeatured(e.target.checked)}
        style={{ marginBottom: 16 }}
      >
        <strong>Show only Featured Products</strong>
      </Checkbox>
      {fetching ? (
        <Spin style={{ width: "100%" }} />
      ) : (
        <DragDropContext
          onDragEnd={(param) => {
            const srcI = param.source.index;
            const destI = param.destination?.index;
            if (destI !== undefined) {
              let temparray = [...datalist];
              temparray.splice(destI, 0, temparray.splice(srcI, 1)[0]);
              setDatalist(temparray);
            }
          }}
        >
          <Droppable droppableId="droppable-1">
            {(provided, _) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{ maxHeight: 400, overflow: "auto" }}
              >
                <List
                  bordered
                  dataSource={datalist}
                  renderItem={(item, i) => (
                    <Draggable key={item._id} draggableId={item._id} index={i}>
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef}>
                          <List.Item
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              boxShadow: snapshot.isDragging
                                ? "0 0 .4rem #666"
                                : "none",
                            }}
                          >
                            <Image
                              width={80}
                              src={image_url + item.image}
                              fallback="https://via.placeholder.com/100"
                            />
                            <span style={{ marginLeft: 16 }}>{item.name}</span>
                            <DragHandle {...provided.dragHandleProps} />
                          </List.Item>
                        </div>
                      )}
                    </Draggable>
                  )}
                />
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Modal>
  );
};

export default Ordering;
